import { CardProps, Divider } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { useNonDeletedLocationSummaries } from 'app/main/settings/locations/useNonDeletedLocationSummaries';
import { FilterUsers } from 'app/main/settings/users/UsersCacheContext';
import { TextLinkButton } from 'framework/components/buttons/TextLinkButton';
import { CardX } from 'framework/components/cards/CardX';
import { FilterOffOutlineIcon } from 'framework/components/icons/FilterOffOutlineIcon';
import { ListFilterComponent } from 'framework/filter/ListFilterComponent';
import { RecordListFilterComponent } from 'framework/filter/RecordListFilterComponent';
import { DateFilterItem } from 'framework/filter/date/DateFilterItem';
import { onResetToPage1 } from 'framework/types/onResetToPage1';
import { clearParams } from 'framework/utils/clearParams';
import { isFiltered } from 'framework/utils/isFiltered';
import { ArticlesInquiryStatusType, ArticlesInquiryStatusTypes, IArticlesInquiriesMetadata, IArticlesInquiriesQueryParams } from 'gen/ApiClient';
import { useLocalization } from 'localization/useLocalization';
import React, { useMemo } from 'react';
import { RecordContext } from 'shared/records/RecordContext';
import { ArticlesInquiriesListPrefilter } from './ArticlesInquiriesListPrefilter';

interface IProps extends CardProps {
	params: IArticlesInquiriesQueryParams;
	setParams: React.Dispatch<React.SetStateAction<IArticlesInquiriesQueryParams>>;
	metadata: IArticlesInquiriesMetadata;
}

export const ArticlesInquiriesListFilterCard = ({ params, setParams, style, metadata, ...rest }: IProps) => {
	const strings = useLocalization();
	const { articlesInquiryStatusTypeRecord } = React.useContext(RecordContext);
	const isFilteredX = useMemo(() => isFiltered(params, []), [params]);
	const [locations] = useNonDeletedLocationSummaries();

	const onFilter = (pars: IArticlesInquiriesQueryParams) => setParams(onResetToPage1(pars));
	const onClear = () => setParams(onResetToPage1(clearParams(params, [])));

	return (
		<CardX
			{...rest}
			style={style}
			className='df-col gap-8'>
			<div className='df-row-ac'>
				<span style={{ fontWeight: '500' }}>{strings.filter}</span>
			</div>
			<ArticlesInquiriesListPrefilter
				selectedPrefilter={params.prefilter}
				setSelectedPrefilter={key => onFilter({ ...params, prefilter: key })}
				metadata={metadata}
			/>
			<Divider />
			<TextLinkButton
				startIcon={<FilterOffOutlineIcon />}
				color='secondary'
				hoverColor='secondary'
				disabled={isFilteredX === false}
				onClick={onClear}>
				{strings.clearFilter}
			</TextLinkButton>
			<Divider />
			<div
				className='df-col gap-4'
				style={{ alignItems: 'start', marginLeft: -5 }}>
				<DateFilterItem
					label={strings.dateCreated}
					after={params.createdAfter}
					before={params.createdBefore}
					setSelected={(after, before) => onFilter({ ...params, createdAfter: after, createdBefore: before })}
				/>
				<RecordListFilterComponent<ArticlesInquiryStatusType>
					options={ArticlesInquiryStatusTypes}
					record={articlesInquiryStatusTypeRecord}
					label={strings.status}
					selected={params.statuses as any}
					setSelected={vals => onFilter({ ...params, statuses: vals })}
					icon={<HelpOutlineIcon />}
				/>
				<FilterUsers
					value={params.handlingUserIds}
					onChange={vals => onFilter({ ...params, handlingUserIds: vals })}
					options={{ includeBlocked: true, onlyAudiologists: false }}
					label={strings.user}
					title={strings.user}
				/>
				<ListFilterComponent<string>
					options={locations}
					selected={params.locationIds}
					setSelected={vals => onFilter({ ...params, locationIds: vals })}
					label={strings.location}
					icon={<HelpOutlineIcon />}
					title={strings.location}
				/>
			</div>
		</CardX>
	);
};
